'use client'

import type { ReactNode } from 'react'
import type { DemoSection } from '@/constants/docs-nav'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { useTheme } from '@/components/generals/theme-provider'
import { GoodUnitedLogo } from '@/components/icons/goodunited-logo'
import { DocsSearch } from '@/components/docs/docs-search'
import { DocsSidebarNav } from '@/components/docs/docs-sidebar'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { DEMO_NAV, TOP_NAV } from '@/constants/docs-nav'

import {
    AcquireIcon,
    ActionsIcon,
    CaretDownIcon,
    GrowIcon,
    InsightsIcon,
    ListIcon,
    MagnifyingGlassIcon,
    MoonIcon,
    SettingsIcon,
    SunIcon,
    SupportersIcon
} from '@/components/icons/icon-set'

/* Each demo shows the icon of the sidebar section it lives in. */
const DEMO_ICONS: Record<DemoSection, ReactNode> = {
    insights: <InsightsIcon aria-hidden='true' />,
    actions: <ActionsIcon aria-hidden='true' />,
    acquire: <AcquireIcon aria-hidden='true' />,
    grow: <GrowIcon aria-hidden='true' />,
    supporters: <SupportersIcon aria-hidden='true' />,
    settings: <SettingsIcon aria-hidden='true' />
}

/* Docs top bar: logo, section links, search, light and dark switch, and the phone menu. */
export function SiteHeader() {
    const router = useRouter()
    const { theme, toggleTheme } = useTheme()
    const [searchOpen, setSearchOpen] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const path = router.asPath.split('#')[0].split('?')[0]

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null
            const typing = !!target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
            if ((event.key === 'k' && (event.metaKey || event.ctrlKey)) || (event.key === '/' && !typing)) {
                event.preventDefault()
                setSearchOpen(open => !open)
            }
        }

        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [])

    const isActive = (match: string[]) => match.some(prefix => prefix === '/docs' ? path === '/docs' || path.startsWith('/docs/foundations') : path.startsWith(prefix))

    return (
        <header className='sticky top-0 z-40 w-full border-b border-border-subtle bg-surface-page/95 backdrop-blur supports-[backdrop-filter]:bg-surface-page/80'>
            <div className='mx-auto flex h-14 max-w-384 items-center gap-2 px-4 sm:px-6 lg:px-8'>
                <Button variant='ghost' size='icon' className='-ml-2 lg:hidden' onClick={() => setMenuOpen(true)} aria-label='Open menu'>
                    <ListIcon className='size-5' aria-hidden='true' />
                </Button>
                <Link href='/' aria-label='GoodUnited design system home' className='mr-4 flex shrink-0 items-center rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-border-focus'>
                    <GoodUnitedLogo id='gu-header-logo' className='h-7 w-auto text-text-primary' />
                </Link>
                <nav aria-label='Main' className='hidden items-center gap-1 lg:flex'>
                    {TOP_NAV.map(item => (
                        <Link
                            key={item.href}
                            href={item.href}
                            aria-current={isActive(item.match) ? 'page' : undefined}
                            className={cn(
                                'rounded-md px-3 py-2 text-sm font-medium transition-colors outline-none focus-visible:outline-2 focus-visible:outline-border-focus',
                                isActive(item.match) ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'
                            )}
                        >
                            {item.title}
                        </Link>
                    ))}
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            className={cn(
                                'inline-flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors outline-none focus-visible:outline-2 focus-visible:outline-border-focus',
                                isActive(['/demo']) ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'
                            )}
                        >
                            Demos
                            <CaretDownIcon className='size-4' aria-hidden='true' />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className='min-w-56'>
                            <DropdownMenuGroup>
                                <DropdownMenuLabel>Demo pages</DropdownMenuLabel>
                                {DEMO_NAV.map(item => (
                                    <DropdownMenuItem key={item.href} onClick={() => router.push(item.href)}>
                                        {DEMO_ICONS[item.section]}
                                        {item.title}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuGroup>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </nav>
                <div className='ml-auto flex items-center gap-2'>
                    <button
                        type='button'
                        onClick={() => setSearchOpen(true)}
                        className='flex h-9 cursor-pointer items-center gap-2 rounded-md border border-input-border bg-input-background px-3 text-sm text-text-placeholder transition-colors outline-none hover:border-input-border-hover focus-visible:border-input-border-focus sm:w-56 lg:w-64'
                        aria-label='Search the docs'
                    >
                        <MagnifyingGlassIcon className='size-4 shrink-0' aria-hidden='true' />
                        <span className='hidden flex-1 text-left sm:inline'>
                            Search the docs
                        </span>
                        <kbd className='hidden rounded-sm border border-border-subtle bg-surface-subtle px-1.5 font-mono text-xs text-text-secondary sm:inline'>
                            ⌘K
                        </kbd>
                    </button>
                    <Separator orientation='vertical' className='mx-1 h-5 self-center' />
                    <Button variant='ghost' size='icon' onClick={toggleTheme} aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                        {theme === 'dark' ? <SunIcon className='size-5' aria-hidden='true' /> : <MoonIcon className='size-5' aria-hidden='true' />}
                    </Button>
                </div>
            </div>

            <DocsSearch open={searchOpen} onOpenChange={setSearchOpen} />

            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <SheetContent side='left' className='lg:hidden'>
                    <div className='flex h-14 shrink-0 items-center border-b border-border-subtle px-4'>
                        <GoodUnitedLogo id='gu-menu-logo' className='h-7 w-auto text-text-primary' />
                    </div>
                    <div className='flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4'>
                        <nav aria-label='Main' className='flex flex-col gap-1'>
                            {TOP_NAV.map(item => (
                                <Link
                                    key={item.href}
                                    href={item.href}
                                    onClick={() => setMenuOpen(false)}
                                    aria-current={isActive(item.match) ? 'page' : undefined}
                                    className={cn('flex h-9 items-center rounded-md px-2 text-base font-medium outline-none focus-visible:outline-2 focus-visible:outline-border-focus', isActive(item.match) ? 'text-text-primary' : 'text-text-secondary')}
                                >
                                    {item.title}
                                </Link>
                            ))}
                        </nav>
                        <nav aria-label='Demos' className='flex flex-col gap-1'>
                            <p className='px-2 text-sm font-medium text-text-secondary'>
                                Demos
                            </p>
                            {DEMO_NAV.map(item => (
                                <Link
                                    key={item.href}
                                    href={item.href}
                                    onClick={() => setMenuOpen(false)}
                                    className='flex h-9 items-center rounded-md px-2 text-base font-medium text-text-primary outline-none focus-visible:outline-2 focus-visible:outline-border-focus'
                                >
                                    {item.title}
                                </Link>
                            ))}
                        </nav>
                        <Separator />
                        <DocsSidebarNav onNavigate={() => setMenuOpen(false)} />
                    </div>
                </SheetContent>
            </Sheet>
        </header>
    )
}
